/**
 * The Color — Security Module
 * Bot protection: honeypot injection, timing validation, JS challenge,
 * client-side rate limiting, and user-agent screening.
 * Protects all forms marked with [data-tc-protected].
 */

(function () {
  'use strict';

  var MIN_SUBMIT_TIME_MS = 3000;
  var MAX_SUBMISSIONS = 3;
  var RATE_LIMIT_WINDOW_MS = 5 * 60 * 1000;
  var STORAGE_KEY = 'tc_form_submissions';

  /* ── User-Agent screening ── */

  var BOT_PATTERNS = [
    /headless/i,
    /phantomjs/i,
    /selenium/i,
    /webdriver/i,
    /puppeteer/i,
    /crawl/i,
    /bot(?!tom)/i,
    /spider/i,
    /slurp/i,
    /lighthouse/i
  ];

  function isSuspectedBot() {
    var ua = navigator.userAgent || '';
    for (var i = 0; i < BOT_PATTERNS.length; i++) {
      if (BOT_PATTERNS[i].test(ua)) return true;
    }
    /* Headless browser detection */
    if (navigator.webdriver) return true;
    return false;
  }

  if (isSuspectedBot()) {
    document.documentElement.classList.add('tc-suspected-bot');
    logSecurity('ua_screening', 'Suspected bot detected: ' + navigator.userAgent);
  }

  /* ── Rate limiter ── */

  function getSubmissions() {
    try {
      var data = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
      var now = Date.now();
      /* Prune expired entries */
      return data.filter(function (ts) { return now - ts < RATE_LIMIT_WINDOW_MS; });
    } catch (e) {
      return [];
    }
  }

  function recordSubmission() {
    var subs = getSubmissions();
    subs.push(Date.now());
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(subs));
    } catch (e) { /* storage full — ignore */ }
  }

  function isRateLimited() {
    return getSubmissions().length >= MAX_SUBMISSIONS;
  }

  /* ── Logging ── */

  function logSecurity(type, detail) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[TC Security] ' + type + ': ' + detail);
    }
    /* Push to Google Analytics if available */
    if (typeof gtag === 'function') {
      gtag('event', 'security_block', {
        event_category: 'security',
        event_label: type,
        value: 1
      });
    }
  }

  /* ── Form protection ── */

  function protectForm(wrapper) {
    var form = wrapper.tagName === 'FORM' ? wrapper : wrapper.querySelector('form');
    if (!form) return;

    /* Prevent double-init */
    if (form.hasAttribute('data-tc-secured')) return;
    form.setAttribute('data-tc-secured', 'true');

    var renderTime = Date.now();

    /* 1. Inject honeypot field */
    var honeypot = document.createElement('input');
    honeypot.type = 'text';
    honeypot.name = 'tc_website';
    honeypot.tabIndex = -1;
    honeypot.autocomplete = 'off';
    honeypot.setAttribute('aria-hidden', 'true');
    honeypot.style.cssText = 'position:absolute;left:-9999px;height:0;width:0;overflow:hidden;opacity:0;pointer-events:none;';
    form.insertBefore(honeypot, form.firstChild);

    /* 2. Inject timestamp field */
    var tsField = document.createElement('input');
    tsField.type = 'hidden';
    tsField.name = 'tc_form_ts';
    tsField.value = renderTime.toString(36);
    form.appendChild(tsField);

    /* 3. Inject JS challenge token */
    var tokenField = document.createElement('input');
    tokenField.type = 'hidden';
    tokenField.name = 'tc_js_token';
    tokenField.value = btoa(Date.now().toString(36) + '_tc');
    form.appendChild(tokenField);

    /* 4. Submit handler (capture phase — runs before other handlers) */
    form.addEventListener('submit', function (e) {
      /* Honeypot check */
      if (honeypot.value) {
        e.preventDefault();
        e.stopImmediatePropagation();
        logSecurity('honeypot', 'Honeypot field filled');
        showFakeSuccess(form);
        return;
      }

      /* Timing check */
      var elapsed = Date.now() - renderTime;
      if (elapsed < MIN_SUBMIT_TIME_MS) {
        e.preventDefault();
        e.stopImmediatePropagation();
        logSecurity('timing', 'Form submitted in ' + elapsed + 'ms (min: ' + MIN_SUBMIT_TIME_MS + 'ms)');
        showRateLimitMessage(form, 'Please wait a moment before submitting.');
        return;
      }

      /* Rate limit check */
      if (isRateLimited()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        logSecurity('rate_limit', 'Too many submissions');
        showRateLimitMessage(form, 'Too many submissions. Please try again later.');
        return;
      }

      /* Record this submission */
      recordSubmission();
    }, true); /* capture: true */
  }

  function showFakeSuccess(form) {
    /* Show nothing suspicious to bots — just disable the submit button */
    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Sent!';
    }
  }

  function showRateLimitMessage(form, message) {
    /* Avoid duplicate messages */
    if (form.querySelector('.tc-security-msg')) return;

    var msg = document.createElement('div');
    msg.className = 'tc-security-msg';
    msg.setAttribute('role', 'alert');
    msg.style.cssText = 'background:#fef2f2;border:1px solid #fca5a5;color:#991b1b;padding:0.75rem 1rem;margin-bottom:1rem;font-size:0.875rem;';
    msg.textContent = message;

    form.insertBefore(msg, form.firstChild);

    /* Auto-remove after 5 seconds */
    setTimeout(function () {
      if (msg.parentNode) msg.parentNode.removeChild(msg);
    }, 5000);
  }

  /* ── Initialise ── */

  function init() {
    var protectedElements = document.querySelectorAll('[data-tc-protected]');
    for (var i = 0; i < protectedElements.length; i++) {
      protectForm(protectedElements[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Expose on global namespace */
  window.TheColor = window.TheColor || {};
  window.TheColor.Security = {
    isRateLimited: isRateLimited,
    isSuspectedBot: isSuspectedBot
  };
})();
